import {
  Add,
  ArrowRightOutlined,
  Close,
  DeleteOutlineOutlined,
  Edit,
  Search,
  Tune,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import React, { useEffect, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import { useSelector } from "react-redux";
import "./Requisted.css";

import DeleteModal from "../../components/Modals/DeleteModal";

const BASE_URL = "https://estbn.com/EstbnNew/public/api/portal";

const RequestedEquipment = () => {
  const [show, setShow] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const [dataGridRows, setDataGridRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 15,
  });
  const [sortModel, setSortModel] = useState([]);
  const [filterParams, setFilterParams] = useState({});

  const [equipmentCategories, setEquipmentCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState(null);

  const [equipmentStatuses, setEquipmentStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [statusesError, setStatusesError] = useState(null);

  const [editingItemId, setEditingItemId] = useState(null);

  const [showDeleteEquipmentConfirm, setShowDeleteEquipmentConfirm] =
    useState(false);
  const [equipmentToDeleteId, setEquipmentToDeleteId] = useState(null);
  const [equipmentToDeleteName, setEquipmentToDeleteName] = useState("");

  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      equipmentName: "",
      requiredQuantity: "",
      estimatedPrice: "",
      status: "",
      requestDate: null,
      category_id: "",
      description: "",
    },
  });

  const handleClose = () => {
    setShow(false);
    reset();
    setEditingItemId(null);
  };

  const handleShow = (rowData = null) => {
    if (rowData) {
      setEditingItemId(rowData.id);

      const selectedCategory = Array.isArray(equipmentCategories)
        ? equipmentCategories.find(
            (cat) => cat.name === rowData.equipmentCategory
          )
        : undefined;

      const currentStatusKey = Array.isArray(equipmentStatuses)
        ? equipmentStatuses.find((s) => s.label === rowData.status)?.value
        : undefined;

      setValue("equipmentName", rowData.name);
      setValue("requiredQuantity", rowData.requiredQuantity);
      setValue("estimatedPrice", rowData.estimatedPrice);
      setValue("status", currentStatusKey || "");
      setValue(
        "requestDate",
        rowData.requestDate ? dayjs(rowData.requestDate) : null
      );
      setValue("category_id", selectedCategory ? selectedCategory.id : "");
      setValue("description", rowData.description);

      setEquipmentToDeleteName(rowData.name);
    } else {
      setEditingItemId(null);
      reset();
      setEquipmentToDeleteName("");
    }
    setShow(true);
  };

  const fetchEquipmentCategories = useCallback(async () => {
    if (!token) {
      setCategoriesError(
        "Authentication token not found. Cannot fetch categories."
      );
      setEquipmentCategories([]);
      return;
    }
    setCategoriesLoading(true);
    setCategoriesError(null);
    try {
      const response = await axios.get(
        `${BASE_URL}/equipment-requests/options/categories`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const categoriesData = response.data.data?.categories;

        if (Array.isArray(categoriesData)) {
          setEquipmentCategories(categoriesData);
        } else {
          console.warn(
            "Unexpected category data structure. Expected an array under 'data.categories':",
            response.data
          );
          setCategoriesError(
            "Invalid category data received from API. Check console for details."
          );
          setEquipmentCategories([]);
        }
      } else {
        setCategoriesError(
          response.data.message || "Failed to fetch categories."
        );
        setEquipmentCategories([]);
      }
    } catch (err) {
      console.error("Error fetching categories:", err);
      setCategoriesError(
        err.response?.data?.message ||
          "Error fetching categories. Please try again."
      );
      setEquipmentCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  }, [token]);

  const fetchEquipmentStatuses = useCallback(async () => {
    if (!token) {
      setStatusesError(
        "Authentication token not found. Cannot fetch statuses."
      );
      setEquipmentStatuses([]);
      return;
    }
    setStatusesLoading(true);
    setStatusesError(null);
    try {
      const response = await axios.get(
        `${BASE_URL}/equipment-requests/options/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const statusOptions = response.data.data?.status_options;
        if (statusOptions && typeof statusOptions === "object") {
          const formattedStatuses = Object.entries(statusOptions).map(
            ([key, label]) => ({
              value: key,
              label: label,
            })
          );
          setEquipmentStatuses(formattedStatuses);
        } else {
          console.warn(
            "Unexpected status data structure or data missing 'status_options':",
            response.data
          );
          setStatusesError(
            "Invalid status data received from API. Check console for details."
          );
          setEquipmentStatuses([]);
        }
      } else {
        setStatusesError(response.data.message || "Failed to fetch statuses.");
        setEquipmentStatuses([]);
      }
    } catch (err) {
      console.error("Error fetching statuses:", err);
      setStatusesError(
        err.response?.data?.message ||
          "Error fetching statuses. Please try again."
      );
      setEquipmentStatuses([]);
    } finally {
      setStatusesLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchEquipmentCategories();
    fetchEquipmentStatuses();
  }, [fetchEquipmentCategories, fetchEquipmentStatuses]);

  const fetchEquipmentRequests = useCallback(async () => {
    if (!token) {
      setDataGridRows([]);
      setRowCount(0);
      return;
    }

    const queryParams = new URLSearchParams();
    queryParams.append("page", paginationModel.page + 1);
    queryParams.append("per_page", paginationModel.pageSize);

    if (sortModel.length > 0) {
      queryParams.append("sort_by", sortModel[0].field);
      queryParams.append("sort_direction", sortModel[0].sort);
    }

    Object.entries(filterParams).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        queryParams.append(key, value.join(","));
      } else if (value) {
        queryParams.append(key, value);
      }
    });

    try {
      const response = await axios.get(
        `${BASE_URL}/equipment-requests?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const apiData = response.data.data.equipment_requests.data;
        const total = response.data.data.equipment_requests.total;

        const formattedRows = apiData.map((item) => ({
          id: item.id,
          name: item.equipment_name || `Equipment ${item.id}`,
          requiredQuantity: parseFloat(item.quantity) || 0,
          estimatedPrice: parseFloat(item.estimated_price) || 0,
          status: item.status_details?.label || item.status || "Unknown",
          requestDate: item.requested_date || "N/A",
          equipmentCategory: item.category?.name || "N/A",
          description: item.description || "",
        }));
        setDataGridRows(formattedRows);
        setRowCount(total);
      } else {
        setDataGridRows([]);
        setRowCount(0);
      }
    } catch (err) {
      console.error("Error fetching equipment requests:", err);
      setDataGridRows([]);
      setRowCount(0);
    }
  }, [
    token,
    paginationModel.page,
    paginationModel.pageSize,
    sortModel,
    filterParams,
  ]);

  useEffect(() => {
    fetchEquipmentRequests();
  }, [fetchEquipmentRequests]);

  const handleSaveEquipment = handleSubmit(async (data) => {
    if (!token) {
      console.error(
        "Authentication token not found. Cannot save equipment request."
      );
      return;
    }

    const mainFormData = new FormData();
    mainFormData.append("equipment_name", data.equipmentName);
    mainFormData.append("quantity", data.requiredQuantity);
    mainFormData.append("estimated_price", data.estimatedPrice);
    mainFormData.append("status", data.status);
    mainFormData.append(
      "request_date",
      data.requestDate ? dayjs(data.requestDate).format("YYYY-MM-DD") : ""
    );
    mainFormData.append("category_id", data.category_id);
    mainFormData.append("description", data.description);

    try {
      let response;
      if (editingItemId) {
        mainFormData.append("_method", "PUT");
        response = await axios.post(
          `${BASE_URL}/equipment-requests/${editingItemId}`,
          mainFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        response = await axios.post(
          `${BASE_URL}/equipment-requests`,
          mainFormData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      if (response.data.success) {
        console.log(
          `Equipment request ${
            editingItemId ? "updated" : "added"
          } successfully!`
        );
        handleClose();
        fetchEquipmentRequests();
      } else {
        console.error(
          `Failed to ${editingItemId ? "update" : "add"} equipment request:`,
          response.data.message || "Unknown error."
        );
        if (response.data.errors) {
          Object.entries(response.data.errors).forEach(([field, errors]) => {
            console.error(`${field}: ${errors.join(", ")}`);
          });
        }
      }
    } catch (err) {
      console.error(
        `Error ${editingItemId ? "updating" : "adding"} equipment request:`,
        err
      );
      if (err.response) {
        console.error(
          "Server responded with error status:",
          err.response.status
        );
        console.error("Server error data:", err.response.data);
        if (err.response.data.errors) {
          const errorMessages = Object.entries(err.response.data.errors)
            .map(([field, errors]) => `${field}: ${errors.join(", ")}`)
            .join("\n");
          console.error(`Validation errors:\n${errorMessages}`);
        }
      } else if (err.request) {
        console.error("No response received from server:", err.request);
      } else {
        console.error("Error setting up request:", err.message);
      }
    }
  });

  const handleDeleteEquipmentShow = (id, name) => {
    setEquipmentToDeleteId(id);
    setEquipmentToDeleteName(name);
    setShowDeleteEquipmentConfirm(true);
  };

  const handleDeleteEquipmentClose = () => {
    setShowDeleteEquipmentConfirm(false);
    setEquipmentToDeleteId(null);
    setEquipmentToDeleteName("");
  };

  const handleDeleteEquipmentConfirm = async () => {
    handleDeleteEquipmentClose();
    if (!token) {
      console.error(
        "Authentication token not found. Cannot delete equipment request."
      );
      return;
    }
    if (!equipmentToDeleteId) {
      console.error("No equipment ID specified for deletion.");
      return;
    }

    try {
      const response = await axios.delete(
        `${BASE_URL}/equipment-requests/${equipmentToDeleteId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        console.log(
          `Equipment request (ID: ${equipmentToDeleteId}) deleted successfully!`
        );
        fetchEquipmentRequests();
      } else {
        console.error(
          `Failed to delete equipment request (ID: ${equipmentToDeleteId}):`,
          response.data.message || "Unknown error."
        );
      }
    } catch (err) {
      console.error(
        `Error deleting equipment request (ID: ${equipmentToDeleteId}):`,
        err
      );
      if (err.response) {
        console.error(
          "Server responded with error status:",
          err.response.status
        );
        console.error("Server error data:", err.response.data);
      } else if (err.request) {
        console.error("No response received from server:", err.request);
      } else {
        console.error("Error setting up delete request:", err.message);
      }
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilterParams((prev) => {
      const currentValues = prev[filterType] || [];
      if (currentValues.includes(value)) {
        return {
          ...prev,
          [filterType]: currentValues.filter((item) => item !== value),
        };
      } else {
        return { ...prev, [filterType]: [...currentValues, value] };
      }
    });
  };

  const handleResetFilters = () => {
    setFilterParams({});
  };

  const applyFilters = () => {
    fetchEquipmentRequests();
    setIsFilterOpen(false);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.3, minWidth: 80 },
    { field: "name", headerName: "Equipment Name", flex: 1, minWidth: 150 },
    {
      field: "requiredQuantity",
      headerName: "Required Quantity",
      flex: 1,
      minWidth: 150,
      type: "number",
    },
    {
      field: "estimatedPrice",
      headerName: "Estimated Price",
      flex: 1,
      minWidth: 150,
      type: "number",
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 105, // Adjusted minWidth to match new fixed width
      renderCell: (params) => {
        const statusLabel = params.value;
        let bgColor = "";
        let textColor = "";
        let borderColor = ""; // New: For the border around the pill

        switch (statusLabel) {
          case "Pending":
            bgColor = "#FFFAEA"; // Your specified background for Pending
            textColor = "#000"; // Black text for light background
            borderColor = "#FEB91E"; // Your specified border color for Pending
            break;
          case "Approved":
            bgColor = "#28a745";
            textColor = "#fff";
            borderColor = "#28a745"; // Matching border color
            break;
          case "Rejected":
            bgColor = "#dc3545";
            textColor = "#fff";
            borderColor = "#dc3545"; // Matching border color
            break;
          case "Ordered":
            bgColor = "#0dcaf0";
            textColor = "#fff";
            borderColor = "#0dcaf0"; // Matching border color
            break;
          case "Received":
            bgColor = "#17a2b8";
            textColor = "#fff";
            borderColor = "#17a2b8"; // Matching border color
            break;
          case "In Review":
            bgColor = "#6f42c1";
            textColor = "#fff";
            borderColor = "#6f42c1"; // Matching border color
            break;
          default:
            bgColor = "#6c757d";
            textColor = "#fff";
            borderColor = "#6c757d"; // Matching border color
        }
        return (
          <span
            style={{
              backgroundColor: bgColor,
              color: textColor,
              border: `1px solid ${borderColor}`, // Apply dynamic border
              width: "105px", // Fixed width
              height: "26px", // Fixed height
              borderRadius: "150px", // Pill shape
              display: "inline-flex", // Use flex to center text vertically and horizontally
              justifyContent: "center",
              alignItems: "center",
              padding: "2px 8px", // Fixed padding
              fontSize: "0.75rem", // Slightly smaller font to ensure text fits
              boxSizing: "border-box", // Include padding and border in the element's total width and height
            }}
          >
            {statusLabel}
          </span>
        );
      },
    },
    {
      field: "requestDate",
      headerName: "Request Date",
      flex: 0.7,
      minWidth: 80,
    },
    {
      field: "equipmentCategory",
      headerName: "Equipment Category",
      flex: 1,
      minWidth: 130,
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        return (
          <div className="d-flex justify-content-evenly align-items-center h-100 w-100">
            <IconButton
              sx={{ color: "primary.main" }}
              aria-label="edit"
              size="small"
              onClick={() => handleShow(params.row)}
            >
              <Edit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteEquipmentShow(params.row.id, params.row.name);
              }}
              aria-label="delete"
              size="small"
              color="error"
            >
              <DeleteOutlineOutlined />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                console.log("Navigate to details for ID:", params.row.id);
              }}
              aria-label="navigate"
              size="small"
              color="default"
            >
              <ArrowRightOutlined />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Drawer
        anchor="right"
        open={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
      >
        <Box sx={{ width: 280 }} role="presentation">
          <div className="filter-container border-bottom py-2 px-3">
            <div className="d-flex justify-content-between align-items-center">
              <Button
                className="bg-transparent text-primary p-0"
                onClick={handleResetFilters}
              >
                Reset All
              </Button>
              <Typography variant="h6" className="m-0  fw-semibold">
                Filter by
              </Typography>
              <IconButton onClick={() => setIsFilterOpen(false)}>
                <Close />
              </IconButton>
            </div>
          </div>
          <div className="filter-sections p-3">
            <div className="" id="accordionExample">
              <div className="border-bottom mb-2">
                <h5 className="accordion-header remove-border-focus">
                  <button
                    className="accordion-button collapsed bg-transparent shadow-none px-0 py-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-condition`}
                    aria-expanded="false"
                    aria-controls={`collapse-condition`}
                  >
                    <Typography
                      variant="subtitle1"
                      component="h5"
                      className="m-0"
                    >
                      Condition
                    </Typography>
                  </button>
                </h5>
                <div
                  id={`collapse-condition`}
                  className="accordion-collapse collapse"
                  data-bs-parent={`#accordionExample`}
                >
                  <div className="accordion-body px-0 py-2">
                    {["Excellent", "Good", "Fair", "Poor", "Damage"].map(
                      (condition) => (
                        <div
                          key={condition}
                          className="d-flex gap-2 main-text-color mb-2"
                        >
                          <input
                            type="checkbox"
                            checked={(filterParams.condition || []).includes(
                              condition
                            )}
                            onChange={() =>
                              handleFilterChange("condition", condition)
                            }
                          />
                          <Typography variant="body2" className="m-0">
                            {condition}
                          </Typography>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>

              <div className="border-bottom mb-2">
                <h5 className="accordion-header remove-border-focus">
                  <button
                    className="accordion-button collapsed bg-transparent shadow-none px-0 py-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-status`}
                    aria-expanded="false"
                    aria-controls={`collapse-status`}
                  >
                    <Typography
                      variant="subtitle1"
                      component="h5"
                      className="m-0"
                    >
                      Status
                    </Typography>
                  </button>
                </h5>
                <div
                  id={`collapse-status`}
                  className="accordion-collapse collapse"
                  data-bs-parent={`#accordionExample`}
                >
                  <div className="accordion-body px-0 py-2">
                    {equipmentStatuses.map((status) => (
                      <div
                        key={status.value}
                        className="d-flex gap-2 main-text-color mb-2"
                      >
                        <input
                          type="checkbox"
                          checked={(filterParams.status || []).includes(
                            status.value
                          )}
                          onChange={() =>
                            handleFilterChange("status", status.value)
                          }
                        />
                        <Typography variant="body2" className="m-0">
                          {status.label}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-bottom mb-2">
                <h5 className="accordion-header remove-border-focus">
                  <button
                    className="accordion-button collapsed bg-transparent shadow-none px-0 py-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-acquisition-type`}
                    aria-expanded="false"
                    aria-controls={`collapse-acquisition-type`}
                  >
                    <Typography
                      variant="subtitle1"
                      component="h5"
                      className="m-0"
                    >
                      Acquisition Type
                    </Typography>
                  </button>
                </h5>
                <div
                  id={`collapse-acquisition-type`}
                  className="accordion-collapse collapse"
                  data-bs-parent={`#accordionExample`}
                >
                  <div className="accordion-body px-0 py-2">
                    {["Purchase", "Rental", "Lease", "Retired"].map((type) => (
                      <div
                        key={type}
                        className="d-flex gap-2 main-text-color mb-2"
                      >
                        <input
                          type="checkbox"
                          checked={(
                            filterParams.acquisition_type || []
                          ).includes(type)}
                          onChange={() =>
                            handleFilterChange("acquisition_type", type)
                          }
                        />
                        <Typography variant="body2" className="m-0">
                          {type}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border-bottom mb-2">
                <h5 className="accordion-header remove-border-focus">
                  <button
                    className="accordion-button collapsed bg-transparent shadow-none px-0 py-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse-warranty`}
                    aria-expanded="false"
                    aria-controls={`collapse-warranty`}
                  >
                    <Typography
                      variant="subtitle1"
                      component="h5"
                      className="m-0"
                    >
                      Warranty
                    </Typography>
                  </button>
                </h5>
                <div
                  id={`collapse-warranty`}
                  className="accordion-collapse collapse"
                  data-bs-parent={`#accordionExample`}
                >
                  <div className="accordion-body px-0 py-2">
                    {[
                      "Less than 3 months",
                      "From 1 to 2 years",
                      "From 2 to 3 years",
                      "From 3 to 4 years",
                      "More than 4 years",
                    ].map((warranty) => (
                      <div
                        key={warranty}
                        className="d-flex gap-2 main-text-color mb-2"
                      >
                        <input
                          type="checkbox"
                          checked={(filterParams.warranty || []).includes(
                            warranty
                          )}
                          onChange={() =>
                            handleFilterChange("warranty", warranty)
                          }
                        />
                        <Typography variant="body2" className="m-0">
                          {warranty}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "center",
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <Button
              variant="contained"
              className="btn btn-dark px-5"
              onClick={applyFilters}
            >
              Show Results
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Modal
        show={show}
        onHide={handleClose}
        dialogClassName="custom-modal-size-md"
      >
        <Modal.Header style={{ backgroundColor: "#D5D5D5" }}>
          <Modal.Title className="text-black">
            {editingItemId ? "Edit Equipment" : "Add Equipment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSaveEquipment}>
            <div className="row ">
              <div className="col-12 col-lg-4">
                <div className="">
                  <label htmlFor="equipmentName">Equipment Name</label>
                  <Controller
                    name="equipmentName"
                    control={control}
                    rules={{ required: "Equipment Name is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="equipmentName"
                        size="small"
                        className="w-100 mt-1"
                        type="text"
                        placeholder="Enter Equipment Name"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="">
                  <label htmlFor="requiredQuantity">Required Quantity</label>
                  <Controller
                    name="requiredQuantity"
                    control={control}
                    rules={{
                      required: "Required Quantity is required",
                      min: { value: 1, message: "Quantity must be at least 1" },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="requiredQuantity"
                        size="small"
                        className="w-100 mt-1 "
                        type="number"
                        placeholder="Enter Required Quantity"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="">
                  <label htmlFor="estimatedPrice">Estimated Price</label>
                  <Controller
                    name="estimatedPrice"
                    control={control}
                    rules={{
                      required: "Estimated Price is required",
                      min: { value: 0, message: "Price cannot be negative" },
                    }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        id="estimatedPrice"
                        size="small"
                        className="w-100 mt-1 "
                        type="number"
                        placeholder="Select Estimated Price"
                        error={!!error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-lg-4">
                <div className="">
                  <label htmlFor="statusSelect">Status</label>
                  <Controller
                    name="status"
                    control={control}
                    rules={{ required: "Status is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        className="w-100 mt-1"
                        size="small"
                        error={!!error}
                      >
                        <Select
                          {...field}
                          id="statusSelect"
                          displayEmpty
                          inputProps={{ "aria-label": "Select Status" }}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 200, overflowY: "scroll" },
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {statusesLoading
                              ? "Loading statuses..."
                              : "Select Status"}
                          </MenuItem>
                          {statusesError && (
                            <MenuItem
                              value=""
                              disabled
                              style={{ color: "red" }}
                            >
                              {statusesError}
                            </MenuItem>
                          )}
                          {equipmentStatuses.map((statusOption) => (
                            <MenuItem
                              key={statusOption.value}
                              value={statusOption.value}
                            >
                              {statusOption.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography variant="caption" color="error">
                            {error.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="">
                  <label className="mt-1" htmlFor="requestDate">
                    Request Date
                  </label>
                  <div className="date-remove-space">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DemoContainer components={["DatePicker"]}>
                        <Controller
                          name="requestDate"
                          control={control}
                          rules={{ required: "Request Date is required" }}
                          render={({ field, fieldState: { error } }) => (
                            <DatePicker
                              {...field}
                              id="requestDate"
                              slotProps={{
                                textField: {
                                  size: "small",
                                  error: !!error,
                                  helperText: error?.message,
                                },
                              }}
                              label="Select Date"
                            />
                          )}
                        />
                      </DemoContainer>
                    </LocalizationProvider>
                  </div>
                </div>
              </div>
              <div className="col-12 col-lg-4">
                <div className="">
                  <label htmlFor="categorySelect">Category</label>
                  <Controller
                    name="category_id"
                    control={control}
                    rules={{ required: "Category is required" }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        className="w-100 mt-1"
                        size="small"
                        error={!!error}
                      >
                        <Select
                          {...field}
                          id="categorySelect"
                          displayEmpty
                          inputProps={{ "aria-label": "Select Category" }}
                          MenuProps={{
                            PaperProps: {
                              style: { maxHeight: 200, overflowY: "scroll" },
                            },
                          }}
                        >
                          <MenuItem value="" disabled>
                            {categoriesLoading
                              ? "Loading categories..."
                              : "Select Category"}
                          </MenuItem>
                          {categoriesError && (
                            <MenuItem
                              value=""
                              disabled
                              style={{ color: "red" }}
                            >
                              {categoriesError}
                            </MenuItem>
                          )}
                          {equipmentCategories.map((cat) => (
                            <MenuItem key={cat.id} value={cat.id}>
                              {cat.name}
                            </MenuItem>
                          ))}
                        </Select>
                        {error && (
                          <Typography variant="caption" color="error">
                            {error.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="my-2">
              <label htmlFor="equipmentDescription">
                Equipment Description
              </label>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    id="equipmentDescription"
                    style={{
                      padding: 10,
                      borderRadius: 15,
                      border: "solid 2px #D8D8D8",
                    }}
                    rows={3}
                    className="w-100 my-1"
                    placeholder="Add Description"
                  />
                )}
              />
            </div>

            <div className="d-flex gap-3 justify-content-center align-items-center custom-card-sm my-3">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className="btn btn-dark px-5"
              >
                {editingItemId ? "Update" : "Save"}
              </Button>
              <Button
                type="button"
                onClick={handleClose}
                variant="outlined"
                color="secondary"
                className="btn btn-light px-5"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <div className="m-4">
        <div className="d-flex justify-content-between align-items-center w-100">
          <Typography variant="h6" className="m-0">
            Equipment Requests
          </Typography>
          <Button
            onClick={() => handleShow()}
            variant="contained"
            className="btn btn-light mx-1"
          >
            <Add /> Add Equipment
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-center w-100 my-2">
          <div className="input-group flex-nowrap justify-content-end align-items-center rounded-2 w-50">
            <input
              type="text"
              className={
                "form-control bg-transparent position-relative rounded"
              }
              placeholder={"Search for equipment .."}
              aria-label="Search"
              aria-describedby="addon-wrapping"
              onChange={(e) => {
                console.log("Search input:", e.target.value);
              }}
            />
            <div className="position-absolute mx-2">
              <Search />
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-end gap-2 w-50">
            <div className="">
              <Button
                onClick={() => setIsFilterOpen(true)}
                variant="outlined"
                className="btn btn-light"
              >
                <Tune />
              </Button>
            </div>
          </div>
        </div>
        <div className="data-grid-small my-4">
          <Paper sx={{ height: "auto", width: "100%" }}>
            <DataGrid
              autoHeight
              rows={dataGridRows}
              columns={columns}
              rowCount={rowCount}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              paginationMode="server"
              sortingMode="server"
              onSortModelChange={setSortModel}
              sx={{ border: 0 }}
              checkboxSelection
              disableRowSelectionOnClick
              pageSizeOptions={[5, 10, 15, 25, 50, 100]}
              getRowId={(row) => row.id}
            />
          </Paper>
        </div>
      </div>

      <DeleteModal
        showDelete={showDeleteEquipmentConfirm}
        handleDeleteClose={handleDeleteEquipmentClose}
        textMessage={`Are you sure you want to delete equipment request "${equipmentToDeleteName}"? This action cannot be undone.`}
        deleteFunction={handleDeleteEquipmentConfirm}
      />
    </>
  );
};

export default RequestedEquipment;
